import React, { useEffect } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { track } from '../modules/tracking';
import { NUTRITION_ADULT_REQUIREMENT } from '../modules/constants';
import PropTypes from 'prop-types';
import { PrintAllergensButton } from './allergens/printAllergensButton';
import * as queryString from 'query-string';

import {
    Allergens,
    AllergenInfo,
    Bastes,
    NutritionalInfo,
    Fulfilment,
    MenuInner,
    Nandinos,
    NutritionFacts,
    Sides
} from '.';

import {
    INFORMATION_PLEASE_CHECK,
    INFORMATION_COMBO,
    INFORMATION_CUSTOMIZABLE_ADD_REMOVE,
    ALLERGEN_TAB,
    NUTRITION_TAB,
    SPICES,
    START_ORDER,
    CLOSE,
    PRODUCT_DESCRIPTION_FOR,
    SIDES_TITLE
} from '../modules/constants';

const COMPONENT_NAME = 'PDP';
const COMPONENT_CLASS_NAME = 'pdp';
const COMPONENT_CLASS_NAMES = 'pdp pdp';
const CLOSE_CLASS = 'close';

function toggle(e) {
    e.target.classList.toggle('open');
    const open = e.target.classList.contains('open');
    track(
        `PDP Click: ${open ? 'Open' : 'Close'} ${e.target.innerHTML
        } Accordion in: ${this}`
    )();
}

/**
 * Displays the Product Details Page (PDP)
 *
 * @function
 * @param       {object} props properties for this page,
 * @constructor
 */
function PDP(props) {
    if (!props) {
        return null;
    }

    const { setPdpInfo, pdpInfo, setShade, hideCalories } = props;

    function closePDP() {
        const pdp = document.querySelector('.pdp');
        if (pdp) {
            pdp.classList.add('close');
            setTimeout(() => setPdpInfo(null), 100);
        } else {
            setPdpInfo(null);
        }
    }

    function showPrintAllergenButton() {
        const { allergen_printing } = queryString.parse(location.search);
        return allergen_printing === 'true';
    }

    function escFunction(e) {
        if (e.keyCode === 27) {
            track(`PDP: Escape key: ${CLOSE}: ${this}`)();
            closePDP();
        }
    }

    function backFunction(e) {
        e.preventDefault();
        e.stopPropagation();
        track(`PDP: Browser back button: ${CLOSE}: ${this}`)();
        closePDP();
    }

    useEffect(() => {
        const goback = backFunction.bind(`${(pdpInfo || {}).displayName}`);
        const escape = escFunction.bind(`${(pdpInfo || {}).displayName}`);
        document.removeEventListener('keyup', escape, false);
        window.removeEventListener('popstate', goback, false);

        if (pdpInfo && pdpInfo !== null) {
            if (typeof document === 'undefined') {
                return;
            }

            setShade({
                shade: true,
                opener: COMPONENT_NAME,
                onClick: () => {
                    track(
                        `Shade: Click: ${CLOSE} PDP: ${(pdpInfo || {}).displayName
                        }`
                    )();
                    closePDP();
                }
            });

            history.pushState(
                { pdpOpen: true, pdpTitle: pdpInfo.displayName },
                'pdp',
                ''
            );

            setTimeout(() => {
                document.addEventListener('keyup', escape, false);
                window.addEventListener('popstate', goback, false);
            }, 250); // MER-267 - bind listeners after pdp has opened
        } else if (pdpInfo === null) {
            setShade({ shade: false, closer: COMPONENT_NAME });
            if (history && history.state && history.state.pdpOpen) {
                history.back();
            }
        }
        return () => {
            document.removeEventListener('keyup', escape, false);
            window.removeEventListener('popstate', goback, false);
        };
    }, [pdpInfo]);

    if (!pdpInfo) {
        return null;
    }

    const { idx, fulfilmentTypes, modifiers } = pdpInfo;

    // https://nandosuk.atlassian.net/wiki/spaces/MR/pages/1854308638/Allergen+display+logic
    // This chunk of code comes directly from the app
    /* eslint-disable */

    const nutritional = [];
    const allergen = [];
    const hasSideOptions = (modifiers) =>
        modifiers.some(({ slug }) => slug === 'choose-side');
    const isCustomizable = (modifiers) =>
        !modifiers.every(({ slug }) => slug === 'choose-size-side');
    const isSide = (modifiers) =>
        modifiers.some(({ slug }) => slug === 'choose-size-side');
    const message = ((pdpInfo || {}).nutritionalInfo || {}).messages || [];

    if (isSide(modifiers)) {
        // nutritional.push(INFORMATION_DISCLAIMER_SIDE);
    }

    allergen.push(INFORMATION_PLEASE_CHECK);
    nutritional.push(INFORMATION_PLEASE_CHECK);
    //allergen.push(INFORMATION_CALL_REST);

    if (isCustomizable(modifiers)) {
        allergen.push(INFORMATION_COMBO);
        nutritional.push(INFORMATION_COMBO);
    }

    if (
        hasSideOptions(modifiers) &&
        pdpInfo.sectionName !== 'Sharing Platters'
    ) {
        allergen.push(INFORMATION_CUSTOMIZABLE_ADD_REMOVE);
        nutritional.push(INFORMATION_CUSTOMIZABLE_ADD_REMOVE);
    }

    if (
        pdpInfo &&
        pdpInfo.nutritionalInfo &&
        pdpInfo.nutritionalInfo.messages
    ) {
        allergen.push(...message);
        nutritional.push(...message);
    }

    let baste = modifiers.find((item) => item.slug.includes('choose-baste'));
    const side = modifiers.find((item) => item.slug.includes('choose-side'));

    if (pdpInfo.slug === 'corn-on-the-cob') {
        baste = -1;
    }

    /* eslint-enable */
    const ourClassName =
        pdpInfo.image && !pdpInfo.noPhotos
            ? COMPONENT_CLASS_NAME + ' image p' + idx
            : COMPONENT_CLASS_NAMES + idx;
    const orderLink =
        props.market === 'UK'
            ? 'https://www.nandos.co.uk/order/search'
            : 'https://www.nandos.ie/order/search';
    return (
        <div className={ourClassName}>
            <a
                className={CLOSE_CLASS}
                title={CLOSE}
                onClick={() => {
                    track(
                        `PDP: Click: Close button: ${(pdpInfo || {}).displayName
                        }`
                    )();
                    closePDP();
                }}
            >
                {`${CLOSE} ${PRODUCT_DESCRIPTION_FOR} ${pdpInfo.displayName}`}
            </a>
            <div className='inner'>
                {pdpInfo.image ? (
                    <div className='hero'>
                        <img
                            src={pdpInfo.image.aspectRatio_16_9.res640x360}
                            alt={
                                pdpInfo.image.altText ||
                                `Image of ${pdpInfo.displayName}`
                            }
                        />
                    </div>
                ) : null}
                {showPrintAllergenButton() ? (
                    <PrintAllergensButton pdpInfo={pdpInfo} />
                ) : null}
                <MenuInner {...pdpInfo} short={false} />
                {pdpInfo.nandinos ? (
                    <Nandinos
                        modifiers={modifiers}
                        bastes={pdpInfo.bastes}
                        toggle={toggle.bind(pdpInfo.displayName)}
                        sides={pdpInfo.possibleSides}
                        item={pdpInfo.displayName}
                        hideCalories={hideCalories}
                    />
                ) : (
                    <>
                        <Bastes
                            toggle={toggle.bind(pdpInfo.displayName)}
                            baste={(baste || {}).options}
                            bastes={pdpInfo.bastes}
                            title={SPICES}
                            hideCalories={hideCalories}
                        />
                    </>
                )}
                {side && (
                    <Sides
                        title={SIDES_TITLE}
                        side={side.options}
                        toggle={toggle.bind(pdpInfo.displayName)}
                    />
                )}
                <Tabs className='tabs'>
                    <TabList className='tablist'>
                        <Tab
                            onClick={track(
                                `PDP: Click: Open: Allergen tab ${pdpInfo.displayName}`
                            )}
                        >
                            {ALLERGEN_TAB}
                        </Tab>
                        <Tab
                            onClick={track(
                                `PDP: Click: Open: Nutrition tab ${pdpInfo.displayName}`
                            )}
                        >
                            {NUTRITION_TAB}
                        </Tab>
                    </TabList>

                    <TabPanel className='tabpanel'>
                        <AllergenInfo allergen={allergen} />
                        <Allergens {...pdpInfo} />
                    </TabPanel>

                    <TabPanel className='tabpanel'>
                        <NutritionalInfo nutritional={nutritional} />
                        <NutritionFacts {...pdpInfo} />
                        <div>
                            <p>{NUTRITION_ADULT_REQUIREMENT}</p>
                        </div>
                    </TabPanel>
                </Tabs>
                {
                    //<Modifiers {...info} />
                }
                <Fulfilment
                    fulfilmentTypes={fulfilmentTypes.sort().toString()}
                />
            </div>
            <a
                href={orderLink}
                className='main cta'
                onClick={track('PDP: Click: Open: Start your order CTA')}
            >
                {START_ORDER}
            </a>
        </div>
    );
}

PDP.propTypes = {
    pdpInfo: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    setPdpInfo: PropTypes.func,
    setShade: PropTypes.func,
    hideCalories: PropTypes.bool,
    market: PropTypes.string
};

export { PDP };
